// @flow

import React from 'react';
import glamorous from 'glamorous';
import colorUtils from 'color';
//import 'rc-slider/assets/index.css';
import identity from 'lodash/identity';
import Slider from 'rc-slider';
import FilterLabel from './FilterLabel';
import useSheet from '../../jss';
import { displayRange } from '../../utils/displayUtils';

type Props = {
  currencySymbol?: string,
  disabled?: boolean,
  format?: () => void,
  label: string,
  lowerValue: number,
  nilString?: string,
  onLowerChange: () => void,
  onUpperChange: () => void,
  options: array,
  classes: Object,
  upperValue?: number,
  scheme?: string,
  updateDelay?: number,
};

type State = {
  lowerValue: number,
  upperValue: number,
};

const Wrapper = glamorous.div(
  {
    alignItems: 'stretch',
    display: 'flex',
    flexBasis: '100%',
    flexDirection: 'column',
    justifyContent: 'space-between',
    paddingTop: 5,
    paddingBottom: 5,
  },
  ({ theme, scheme }) => ({
    color: scheme === 'light' ? theme.color.basis1 : theme.color.basis6,
  }),
  ({ theme, disabled }) =>
    (disabled
      ? {
        '& $range': {
          color: theme.color.basis3,
        },
        '& .rc-slider-track': {
          backgroundColor: theme.color.basis3,
        },
        '& .rc-slider-handle': {
          backgroundImage: 'none',
          backgroundColor: theme.color.basis4,
        },
      }
      : {}),
);

const StyledSlider = glamorous(Slider.Range)(({ theme, scheme }) => ({
  height: 5,
  '&.rc-slider-disabled': {
    background: 'none',
    opacity: 0.4,
  },
  '& .rc-slider-handle': {
    border: 'none',
    backgroundColor: 'transparent',
    fontFamily: 'Ionicons',
    '&::before': {
      position: 'absolute',
      top: '50%',
      left: '50%',
      content: '"\\f21b"',
      color: colorUtils(theme.color.primary)
        .lighten(0.5)
        .string(),
      fontSize: 32,
      transform: 'translate3d(-50%, -50%, 0)',
    },
    '&::after': {
      position: 'absolute',
      top: '50%',
      left: '50%',
      content: '"\\f21b"',
      color: theme.color.primary,
      fontSize: 15,
      transform: 'translate3d(-50%, -50%, 0)',
    },
  },
  '& .rc-slider-rail': {
    backgroundColor: scheme === 'light' ? theme.color.basis5 : theme.color.basis2,
  },
  '& .rc-slider-track': {
    backgroundColor: theme.color.primary,
  },
  [theme.mediaQuery.desktopAndUp]: {
    '& .rc-slider-handle': {
      '&::before': {
        fontSize: 24,
      },
      '&::after': {
        fontSize: 12,
      },
    },
  },
}));

class RangeInput extends React.Component {
  props: Props;

  static defaultProps = {
    currencySymbol: undefined,
    disabled: false,
    format: identity,
    nilString: '',
    upperValue: undefined,
    scheme: 'dark',
    updateDelay: 300,
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      lowerValue: props.lowerValue,
      upperValue: props.upperValue,
    };
  }

  componentDidUpdate(prevProps: Props) {
    const nextState = {};

    if (this.props.lowerValue !== prevProps.lowerValue && this.props.lowerValue !== this.state.lowerValue) {
      nextState.lowerValue = this.props.lowerValue;
    }

    if (this.props.upperValue !== prevProps.upperValue && this.props.upperValue !== this.state.upperValue) {
      nextState.upperValue = this.props.upperValue;
    }

    if (Object.keys(nextState).length > 0) {
      this.setState(nextState);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.onUpdate);
  }

  onChange = (newLower, newUpper) => {
    const { onLowerChange, onUpperChange, updateDelay } = this.props;
    const state = {
      lowerValue: newLower,
      upperValue: newUpper,
    };

    const isLowerDifferent = newLower !== this.state.lowerValue;
    const isUpperDifferent = newUpper !== this.state.upperValue;

    this.setState(state);
    clearTimeout(this.onUpdate);
    this.onUpdate = setTimeout(() => {
      if (isLowerDifferent) {
        onLowerChange(newLower);
      }
      if (isUpperDifferent) {
        onUpperChange(newUpper);
      }
    }, updateDelay);
  };

  render() {
    const { disabled, format, label, nilString, options, classes, scheme, currencySymbol } = this.props;
    const { lowerValue, upperValue } = this.state;

    const rangeStr = displayRange(lowerValue, upperValue, {
      format,
      nilString,
      currencySymbol,
    });
    const min = 0;
    const max = options.length - 1;

    return (
      <Wrapper disabled={disabled} scheme={scheme}>
        <FilterLabel scheme={scheme}>
          <span>{label}: </span>
          <span>{rangeStr}</span>
        </FilterLabel>
        <div className={classes.input}>
          <StyledSlider
            value={[options.indexOf(lowerValue), options.indexOf(upperValue)]}
            max={max}
            min={min}
            onChange={([lowerIndex, upperIndex]) => {
              this.onChange(options[lowerIndex], options[upperIndex]);
            }}
            allowCross={false}
            disabled={disabled}
            scheme={scheme}
          />
        </div>
      </Wrapper>
    );
  }
}

function applyStyles(theme: Theme) {
  return {
    input: {
      display: 'flex',
      flexGrow: 1,
      paddingTop: 10,
      paddingRight: 12,
      paddingBottom: 10,
      paddingLeft: 12,
    },
    [theme.mediaQuery.desktopAndUp]: {
      input: {
        paddingRight: 4,
        paddingLeft: 4,
      },
    },
  };
}

export default useSheet(applyStyles)(RangeInput);

